import request from '@/utils/live_requests';

const state = {
  status: 'idle',
  materialGroups: [],
  firstLevelSubcategories: [],
  secondLevelSubcategories: [],
  thirdLevelSubcategories: [],
  isFetchingMaterialGroups: false,
  isFetchingFirstLevelSubcategories: false,
  isFetchingSecondLevelSubcategories: false,
  isFetchingThirdLevelSubcategories: false,
};

const getters = {
  fetchStatus: (state) => state.status,
  getMaterialGroups: (state) => state.materialGroups,
  getFirstLevelSubcategories: (state) => state.firstLevelSubcategories,
  getSecondLevelSubcategories: (state) => state.secondLevelSubcategories,
  getThirdLevelSubcategories: (state) => state.thirdLevelSubcategories,
  isFetchingMaterialGroups: (state) => state.isFetchingMaterialGroups,
  isFetchingFirstLevelSubcategories: (state) => state.isFetchingFirstLevelSubcategories,
  isFetchingSecondLevelSubcategories: (state) => state.isFetchingSecondLevelSubcategories,
  isFetchingThirdLevelSubcategories: (state) => state.isFetchingThirdLevelSubcategories,
};

const mutations = {
  // Status mutations
  fetchRequest: (state) => {
    state.status = 'loading';
  },
  fetchSuccess: (state) => {
    state.status = 'success';
  },
  fetchError: (state) => {
    state.status = 'error';
  },
  setStatus: (state, status) => {
    state.status = status;
  },

  // Material Groups mutations
  setFetchingMaterialGroups: (state, isFetching) => {
    state.isFetchingMaterialGroups = isFetching;
  },
  setMaterialGroups: (state, materialGroups) => {
    state.materialGroups = materialGroups;
  },

  // First Level Subcategories mutations
  setFetchingFirstLevelSubcategories: (state, isFetching) => {
    state.isFetchingFirstLevelSubcategories = isFetching;
  },
  setFirstLevelSubcategories: (state, firstLevelSubcategories) => {
    state.firstLevelSubcategories = firstLevelSubcategories;
  },

  // Second Level Subcategories mutations
  setFetchingSecondLevelSubcategories: (state, isFetching) => {
    state.isFetchingSecondLevelSubcategories = isFetching;
  },
  setSecondLevelSubcategories: (state, secondLevelSubcategories) => {
    state.secondLevelSubcategories = secondLevelSubcategories;
  },

  // Third Level Subcategories mutations
  setFetchingThirdLevelSubcategories: (state, isFetching) => {
    state.isFetchingThirdLevelSubcategories = isFetching;
  },
  setThirdLevelSubcategories: (state, thirdLevelSubcategories) => {
    state.thirdLevelSubcategories = thirdLevelSubcategories;
  },
};

const actions = {
  // Fetch Material Groups
  fetchMaterialGroups: async ({ commit, rootGetters }) => {
    try {
      commit('setFetchingMaterialGroups', true);
      commit('setMaterialGroups', []);
      commit('fetchRequest');

      const response = await request(
        'GET',
        'api/material-groups',
        rootGetters['auth/getToken'],
        {},
        {}
              );

      commit('setMaterialGroups', response.data.items);
      commit('fetchSuccess');
    } catch (error) {
      commit('fetchError');
      throw error;
    } finally {
      commit('setFetchingMaterialGroups', false);
    }
  },

  // Fetch First Level Subcategories
  fetchFirstLevelSubcategories: async ({ commit, rootGetters }) => {
    try {
      commit('setFetchingFirstLevelSubcategories', true);
      commit('setFirstLevelSubcategories', []);
      commit('fetchRequest');

      const response = await request(
        'GET',
        'api/first-level-subcategories',
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('setFirstLevelSubcategories', response.data.items);
      commit('fetchSuccess');
    } catch (error) {
      commit('fetchError');
      throw error;
    } finally {
      commit('setFetchingFirstLevelSubcategories', false);
    }
  },

  // Fetch Second Level Subcategories
  fetchSecondLevelSubcategories: async ({ commit, rootGetters }) => {
    try {
      commit('setFetchingSecondLevelSubcategories', true);
      commit('setSecondLevelSubcategories', []);
      commit('fetchRequest');

      const response = await request(
        'GET',
        'api/second-level-subcategories',
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('setSecondLevelSubcategories', response.data.items);
      commit('fetchSuccess');
    } catch (error) {
      commit('fetchError');
      throw error;
    } finally {
      commit('setFetchingSecondLevelSubcategories', false);
    }
  },

  // Fetch Third Level Subcategories
  fetchThirdLevelSubcategories: async ({ commit, rootGetters }) => {
    try {
      commit('setFetchingThirdLevelSubcategories', true);
      commit('setThirdLevelSubcategories', []);
      commit('fetchRequest');

      const response = await request(
        'GET',
        'api/third-level-subcategories',
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('setThirdLevelSubcategories', response.data.items);
      commit('fetchSuccess');
    } catch (error) {
      commit('fetchError');
      throw error;
    } finally {
      commit('setFetchingThirdLevelSubcategories', false);
    }
  },

  // Create Material Group
  createMaterialGroup: async ({ commit, rootGetters }, payload) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'POST',
        'admin/material-groups',
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Create First Level Subcategory
  createFirstLevelSubcategory: async ({ commit, rootGetters }, payload) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'POST',
        'admin/first-level-subcategories',
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Create Second Level Subcategory
  createSecondLevelSubcategory: async ({ commit, rootGetters }, payload) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'POST',
        'admin/second-level-subcategories',
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Create Third Level Subcategory
  createThirdLevelSubcategory: async ({ commit, rootGetters }, payload) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'POST',
        'admin/third-level-subcategories',
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Update Material Group
  updateMaterialGroup: async ({ commit, rootGetters }, { id, payload }) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'PUT',
        `admin/material-groups/${id}`,
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Delete Material Group
  deleteMaterialGroup: async ({ commit, rootGetters }, id) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'DELETE',
        `admin/material-groups/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Update First Level Subcategory
  updateFirstLevelSubcategory: async ({ commit, rootGetters }, { id, payload }) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'PUT',
        `admin/first-level-subcategories/${id}`,
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Delete First Level Subcategory
  deleteFirstLevelSubcategory: async ({ commit, rootGetters }, id) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'DELETE',
        `admin/first-level-subcategories/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Update Second Level Subcategory
  updateSecondLevelSubcategory: async ({ commit, rootGetters }, { id, payload }) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'PUT',
        `admin/second-level-subcategories/${id}`,
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Delete Second Level Subcategory
  deleteSecondLevelSubcategory: async ({ commit, rootGetters }, id) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'DELETE',
        `admin/second-level-subcategories/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Update Third Level Subcategory
  updateThirdLevelSubcategory: async ({ commit, rootGetters }, { id, payload }) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'PUT',
        `admin/third-level-subcategories/${id}`,
        rootGetters['auth/getToken'],
        payload,
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },

  // Delete Third Level Subcategory
  deleteThirdLevelSubcategory: async ({ commit, rootGetters }, id) => {
    try {
      commit('fetchRequest');

      const response = await request(
        'DELETE',
        `admin/third-level-subcategories/${id}`,
        rootGetters['auth/getToken'],
        {},
        {}
      );

      commit('fetchSuccess');
      return Promise.resolve(response.data);
    } catch (error) {
      commit('fetchError');
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
